import { useCallback, useEffect, useState } from "react";
import { LifeBuoy, Mail, Copy, Loader2, Send, ArrowLeft, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiError } from "@/lib/api";

const SUPPORT_EMAIL = "support@suntoshgroup.com";
const TOPICS = ["Question", "Billing & payments", "Report a problem", "Feature request"];
const field = "w-full border border-ink/15 px-3 py-2.5 text-sm focus:outline-none focus:border-klein";
const when = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const SupportForm = ({ onSent }) => {
    const [f, setF] = useState({ topic: TOPICS[0], subject: "", message: "", phone: "" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/support/enquiry", f);
            toast.success("Message sent. We'll reply here and by email.");
            setF({ topic: TOPICS[0], subject: "", message: "", phone: "" });
            onSent(data.id);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not send your message");
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={submit} className="space-y-3" data-testid="support-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <select value={f.topic} onChange={set("topic")} className={field} data-testid="support-topic">{TOPICS.map((t) => <option key={t}>{t}</option>)}</select>
                <input value={f.phone} onChange={set("phone")} placeholder="Phone (optional)" className={field} data-testid="support-phone" />
            </div>
            <input value={f.subject} onChange={set("subject")} placeholder="Subject" required minLength={3} maxLength={150} className={field} data-testid="support-subject" />
            <textarea value={f.message} onChange={set("message")} placeholder="How can we help?" required minLength={10} maxLength={4000} rows={5} className={`${field} resize-y`} data-testid="support-message" />
            <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-klein text-white py-2.5 text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="support-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {busy ? "Sending…" : "Send message"}
            </button>
        </form>
    );
};

export const SupportThread = ({ doc, onReply, mine = "company", testid = "support-thread" }) => {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const send = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        setBusy(true);
        try {
            await onReply(text.trim());
            setText("");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="flex flex-col gap-3" data-testid={testid}>
            <div className="space-y-2.5 max-h-[45vh] overflow-y-auto pr-1">
                {(doc.messages || []).map((m) => (
                    <div key={m.id} className={`flex ${m.by === mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.by === mine ? "bg-klein text-white rounded-br-sm" : "bg-ink/[0.05] text-ink rounded-bl-sm"}`}>
                            <div className="whitespace-pre-wrap break-words">{m.text}</div>
                            <div className={`mt-1 text-[10px] ${m.by === mine ? "text-white/70" : "text-ink/45"}`}>{m.author} · {when(m.at)}</div>
                        </div>
                    </div>
                ))}
            </div>
            <form onSubmit={send} className="flex items-end gap-2">
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={4000} placeholder="Write a reply…" className={`${field} resize-none flex-1`} data-testid={`${testid}-input`} />
                <button type="submit" disabled={busy || !text.trim()} className="h-10 w-10 inline-flex items-center justify-center bg-klein text-white disabled:opacity-50" aria-label="Send reply" data-testid={`${testid}-send`}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
            </form>
        </div>
    );
};

const MyMessages = ({ openId, setOpenId, reload }) => {
    const [list, setList] = useState(null);
    const [doc, setDoc] = useState(null);
    useEffect(() => { api.get("/support/enquiries").then(({ data }) => setList(data)).catch(() => setList([])); }, [openId]);
    useEffect(() => {
        if (!openId) return setDoc(null);
        api.get(`/support/enquiries/${openId}`).then(({ data }) => { setDoc(data); reload(); }).catch(() => setOpenId(null));
    }, [openId, setOpenId, reload]);
    if (doc) {
        return (
            <div className="space-y-3">
                <button onClick={() => setOpenId(null)} className="inline-flex items-center gap-1 text-xs font-semibold text-klein" data-testid="support-back-btn"><ArrowLeft className="w-3.5 h-3.5" /> All messages</button>
                <div className="flex items-center justify-between gap-2"><div className="font-semibold text-ink truncate">{doc.subject}</div>
                    <span className={`text-[11px] font-semibold uppercase ${doc.status === "solved" ? "text-emerald-700" : "text-amber-700"}`} data-testid="support-thread-status">{doc.status === "solved" ? "Solved" : "Open"}</span></div>
                <SupportThread doc={doc} onReply={async (t) => {
                    try { const { data } = await api.post(`/support/enquiries/${doc.id}/reply`, { message: t }); setDoc(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send"); }
                }} />
            </div>
        );
    }
    if (!list) return <div className="py-6 text-center text-sm text-ink/40">Loading…</div>;
    if (!list.length) return <div className="py-8 text-center text-sm text-ink/40" data-testid="support-my-empty">No messages yet</div>;
    return (
        <div className="space-y-1.5 max-h-[50vh] overflow-y-auto" data-testid="support-my-list">
            {list.map((m) => (
                <button key={m.id} onClick={() => setOpenId(m.id)} data-testid={`support-my-${m.id}`}
                    className="w-full text-left flex items-start gap-3 border border-ink/10 px-3 py-2.5 hover:border-klein/40 transition-colors">
                    <MessageSquare className="w-4 h-4 mt-0.5 text-ink/40 shrink-0" />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2"><span className="text-sm font-semibold text-ink truncate">{m.subject}</span>
                            {m.unread_company && <span className="text-[10px] font-bold uppercase bg-klein text-white px-1.5 py-0.5 rounded-full">New reply</span>}</div>
                        <div className="text-xs text-ink/50">{m.topic} · {when(m.updated_at || m.created_at)} · {m.status === "solved" ? "Solved" : "Open"}</div>
                    </div>
                </button>
            ))}
        </div>
    );
};

// Admin-only bar fixed to the bottom of the screen: support contact, enquiry form and replies.
export const SupportFooter = () => {
    const initial = new URLSearchParams(window.location.search).get("support");
    const [open, setOpen] = useState(!!initial);
    const [tab, setTab] = useState(initial ? "mine" : "new");
    const [openId, setOpenId] = useState(initial);
    const [unread, setUnread] = useState(0);
    const reload = useCallback(() => api.get("/support/enquiries").then(({ data }) => setUnread(data.filter((m) => m.unread_company).length)).catch(() => {}), []);
    useEffect(() => {
        reload();
        const t = setInterval(reload, 60000);
        if (initial) window.history.replaceState(null, "", window.location.pathname);
        return () => clearInterval(t);
    }, [reload]); // eslint-disable-line react-hooks/exhaustive-deps
    const copy = () => navigator.clipboard.writeText(SUPPORT_EMAIL).then(() => toast.success("Email copied"));
    return (
        <footer className="fixed bottom-0 left-0 right-0 lg:left-64 z-30 border-t border-ink/10 bg-white/95 backdrop-blur px-4 sm:px-8 h-11 flex items-center justify-between gap-3 text-xs text-ink/50" data-testid="support-footer">
            <span className="truncate">Suntosh HRM · by Suntosh Group</span>
            <button onClick={() => { setOpen(true); if (unread) setTab("mine"); }} className="inline-flex items-center gap-1.5 font-semibold text-klein hover:underline shrink-0" data-testid="support-open-btn">
                <LifeBuoy className="w-4 h-4" /> <span className="hidden sm:inline">Need help?</span> Contact support
                {unread > 0 && <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta-500 text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="support-unread-badge">{unread}</span>}
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="support-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Contact Suntosh HRM support</DialogTitle>
                        <DialogDescription>Ask a question or report a problem. Replies appear here and in your email.</DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-between gap-2 border border-ink/10 bg-ink/[0.02] px-3 py-2.5 text-sm" data-testid="support-email-box">
                        <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 text-ink hover:text-klein min-w-0" data-testid="support-email-link">
                            <Mail className="w-4 h-4 shrink-0" /> <span className="truncate">{SUPPORT_EMAIL}</span>
                        </a>
                        <button onClick={copy} className="inline-flex items-center gap-1 text-xs font-semibold text-klein shrink-0" data-testid="support-email-copy"><Copy className="w-3.5 h-3.5" /> Copy</button>
                    </div>
                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList className="w-full"><TabsTrigger value="new" className="flex-1" data-testid="support-tab-new">New message</TabsTrigger>
                            <TabsTrigger value="mine" className="flex-1" data-testid="support-tab-mine">My messages{unread ? ` (${unread})` : ""}</TabsTrigger></TabsList>
                        <TabsContent value="new" className="mt-3"><SupportForm onSent={(id) => { setOpenId(id); setTab("mine"); }} /></TabsContent>
                        <TabsContent value="mine" className="mt-3"><MyMessages openId={openId} setOpenId={setOpenId} reload={reload} /></TabsContent>
                    </Tabs>
                </DialogContent>
            </Dialog>
        </footer>
    );
};

export default SupportFooter;
